import React from "react";
import { useDispatch, useSelector } from "react-redux";
import i18n from "../../i18n";
import country from "../../config/countryConfig";
import { branding } from "../../config/portalBranding";
import { languageChangeNavbar } from "../Auth/auth.actions";
import "./LanguageToggle.css";

/**
 * LanguageToggle — pill-style language switcher.
 *
 * Replaces the legacy <select id="language-dropdown"> across all panelist
 * pages (navbar, FAQ AppBar, Login, Registration, BasicProfile). Self-
 * contained: reads everything it needs from Redux + country config +
 * portal branding, so callers just drop in <LanguageToggle />.
 *
 * Design: two (or more) pill buttons joined inside one rounded capsule —
 * active pill filled with the portal brand red (white text), inactive
 * pills transparent (dark text). Mirrors nipponpolls.com/privacyPolicy.php.
 *
 * Wiring (unchanged from the old dropdown's onChange):
 *   - dispatch(languageChangeNavbar(lang))  -> Redux state.adminUser.adminUser.language
 *                                              + writes localStorage('userLanguagePreference')
 *   - i18n.changeLanguage(lang)             -> flips i18next page chrome
 *
 * Which languages render is country-config truth (country.supportedLanguages).
 * If only one language is supported (en-only portals UK/MY/SG/PH) the
 * component renders nothing.
 */

// Human-readable label per language code, shown on each pill.
const LANGUAGE_LABELS = {
  en: "English",
  hi: "हिन्दी",
  jp: "日本語",
};

function LanguageToggle({ className, style }) {
  const dispatch = useDispatch();
  // Redux is the single source of truth for the active language; falls
  // back to the live i18next language, then 'en'.
  const reduxLanguage = useSelector(
    (state) => state.adminUser?.adminUser?.language
  );
  const activeLang = reduxLanguage || i18n.language || "en";

  const supported = Array.isArray(country.supportedLanguages)
    ? country.supportedLanguages
    : ["en"];

  // en-only portals (UK/MY/SG/PH): nothing to toggle — render nothing.
  if (supported.length < 2) {
    return null;
  }

  const brandRed = branding.primaryColor;

  const handleSelect = (lang) => {
    if (lang === activeLang) return;
    // Same two calls the legacy <select>'s onChange made. The action
    // creator persists to localStorage('userLanguagePreference') itself.
    dispatch(languageChangeNavbar(lang));
    i18n.changeLanguage(lang);
  };

  return (
    <div
      className={`dx-langToggle${className ? ` ${className}` : ""}`}
      role="group"
      aria-label="Language"
      style={style}
    >
      {supported.map((lang) => {
        const isActive = lang === activeLang;
        return (
          <button
            key={lang}
            type="button"
            className={`dx-langToggle-pill${
              isActive ? " dx-langToggle-pill--active" : ""
            }`}
            aria-pressed={isActive}
            onClick={() => handleSelect(lang)}
            style={
              isActive
                ? { backgroundColor: brandRed, borderColor: brandRed }
                : undefined
            }
          >
            {LANGUAGE_LABELS[lang] || lang}
          </button>
        );
      })}
    </div>
  );
}

export default LanguageToggle;
